import React, { useEffect, useMemo, useState } from 'react';
import PropTypes from 'prop-types';
import { FiX } from 'react-icons/fi';
import api from '../services/api';

// Design tokens (aligned with Quotes.js modals)
const colors = {
  stone50: '#fafaf9',
  stone100: '#f5f5f4',
  stone200: '#e7e5e4',
  stone400: '#a8a29e',
  stone500: '#78716c',
  stone600: '#57534e',
  stone700: '#44403c',
  stone800: '#292524',
  accent500: '#06b6d4',
  accent600: '#0891b2',
  emerald600: '#059669'
};

const PaymentModal = ({
  isOpen,
  onClose,
  onSubmit,
  title = 'Record Payment',
  submitText = 'Submit',
  submitLoadingText = 'Processing...',
  isSubmitting = false,
  acceptPdfProof = false,
  initialData = {},
  additionalFields = null,
  styles = {}
}) => {
  const computeInitialState = () => ({
    paymentMethod: initialData.paymentMethod || '',
    paymentReference: initialData.paymentReference || '',
    paymentDate: initialData.paymentDate || new Date().toISOString().slice(0, 10),
    proofUrl: initialData.proofUrl || '',
    uploading: false
  });

  const [formData, setFormData] = useState(computeInitialState);

  // Re-hydrate when the modal opens (shared modal instances often have changing initial data)
  useEffect(() => {
    if (!isOpen) return;
    setFormData(computeInitialState());
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isOpen, initialData?.paymentMethod, initialData?.paymentReference, initialData?.paymentDate, initialData?.proofUrl]);

  const handleInputChange = (field, value) => {
    setFormData(prev => ({ ...prev, [field]: value }));
  };

  const handleFileUpload = async (event) => {
    const file = event.target.files?.[0];
    if (!file) return;

    try {
      handleInputChange('uploading', true);
      const resp = await api.get('/api/upload/presigned-url', {
        params: { fileType: file.type }
      });
      const { uploadUrl, fileUrl } = resp.data;
      await fetch(uploadUrl, {
        method: 'PUT',
        headers: { 'Content-Type': file.type },
        body: file
      });
      handleInputChange('proofUrl', fileUrl || uploadUrl.split('?')[0]);
      handleInputChange('uploading', false);
      if (window.toast?.call) window.toast('Proof uploaded');
    } catch (error) {
      console.error('Upload failed', error);
      handleInputChange('uploading', false);
      if (window.toast?.call) window.toast('Failed to upload proof');
    }
  };

  const handleSubmit = async () => {
    if (!formData.paymentMethod) {
      if (window.toast?.call) window.toast('Please select a payment method');
      return;
    }

    const paymentData = {
      paymentMethod: formData.paymentMethod,
      paymentReference: formData.paymentReference || undefined,
      paymentDate: formData.paymentDate || undefined,
      paymentProofUrl: formData.proofUrl || undefined
    };

    try {
      await onSubmit(paymentData);
      // Reset form on successful submission
      setFormData({
        paymentMethod: '',
        paymentReference: '',
        paymentDate: new Date().toISOString().slice(0, 10),
        proofUrl: '',
        uploading: false
      });
    } catch (error) {
      console.error('Payment submission failed', error);
    }
  };

  const handleClose = () => {
    setFormData(computeInitialState());
    onClose();
  };

  const modalStyles = useMemo(() => {
    const base = {
      modal: {
        position: 'fixed',
        inset: 0,
        background: 'rgba(28, 25, 23, 0.6)',
        backdropFilter: 'blur(4px)',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        zIndex: 1000,
        padding: 20
      },
      modalContent: {
        background: '#fff',
        borderRadius: 16,
        width: '100%',
        maxWidth: 520,
        maxHeight: '90vh',
        overflow: 'auto',
        boxShadow: '0 24px 48px rgba(0,0,0,0.2)',
        border: `1px solid ${colors.stone200}`
      },
      modalHeader: {
        padding: '16px 20px',
        borderBottom: `1px solid ${colors.stone200}`,
        display: 'flex',
        justifyContent: 'space-between',
        alignItems: 'center',
        gap: 12
      },
      modalBody: {
        padding: '16px 20px'
      },
      modalFooter: {
        padding: '12px 20px',
        borderTop: `1px solid ${colors.stone100}`,
        display: 'flex',
        justifyContent: 'flex-end',
        gap: 8,
        flexWrap: 'wrap'
      },
      btnIcon: {
        width: 32,
        height: 32,
        padding: 0,
        background: 'transparent',
        border: 'none',
        borderRadius: 8,
        cursor: 'pointer',
        color: colors.stone500,
        display: 'inline-flex',
        alignItems: 'center',
        justifyContent: 'center'
      },
      btnSecondary: {
        padding: '7px 12px',
        fontSize: 12,
        fontWeight: 500,
        color: colors.stone700,
        background: '#fff',
        border: `1.5px solid ${colors.stone200}`,
        borderRadius: 8,
        cursor: 'pointer',
        display: 'inline-flex',
        alignItems: 'center',
        justifyContent: 'center',
        gap: 6
      },
      btnPrimary: {
        padding: '7px 12px',
        fontSize: 12,
        fontWeight: 600,
        color: colors.stone800,
        background: '#fff',
        border: `1.5px solid ${colors.stone200}`,
        borderRadius: 8,
        cursor: 'pointer',
        display: 'inline-flex',
        alignItems: 'center',
        justifyContent: 'center',
        gap: 6,
        boxShadow: 'none'
      },
      formGroup: {
        marginBottom: 12
      },
      label: {
        display: 'block',
        fontSize: 11,
        fontWeight: 600,
        color: colors.stone500,
        textTransform: 'uppercase',
        letterSpacing: '0.05em',
        marginBottom: 4
      },
      input: {
        width: '100%',
        padding: '8px 10px',
        fontSize: 12,
        border: `1.5px solid ${colors.stone200}`,
        borderRadius: 8,
        background: colors.stone50,
        color: colors.stone800,
        outline: 'none',
        boxSizing: 'border-box'
      },
      select: {
        width: '100%',
        padding: '8px 30px 8px 10px',
        fontSize: 12,
        border: `1.5px solid ${colors.stone200}`,
        borderRadius: 8,
        background: '#fff',
        color: colors.stone700,
        cursor: 'pointer',
        outline: 'none',
        appearance: 'none',
        boxSizing: 'border-box',
        backgroundImage: `url("data:image/svg+xml,%3Csvg xmlns='http://www.w3.org/2000/svg' width='12' height='12' viewBox='0 0 24 24' fill='none' stroke='%2378716c' stroke-width='2'%3E%3Cpolyline points='6 9 12 15 18 9'/%3E%3C/svg%3E")`,
        backgroundRepeat: 'no-repeat',
        backgroundPosition: 'right 10px center'
      },
      hint: {
        marginTop: 6,
        fontSize: 11,
        color: colors.stone500
      },
      success: {
        marginTop: 6,
        fontSize: 11,
        color: colors.stone600
      }
    };

    // Allow callers to override styles (keeps API compatible)
    return {
      modal: { ...base.modal, ...(styles.modal || {}) },
      modalContent: { ...base.modalContent, ...(styles.modalContent || {}) },
      modalHeader: { ...base.modalHeader, ...(styles.modalHeader || {}) },
      modalBody: { ...base.modalBody, ...(styles.modalBody || {}) },
      modalFooter: { ...base.modalFooter, ...(styles.modalFooter || {}) },
      btnIcon: { ...base.btnIcon, ...(styles.btnIcon || {}) },
      btnSecondary: { ...base.btnSecondary, ...(styles.btnSecondary || {}) },
      btnPrimary: { ...base.btnPrimary, ...(styles.btnPrimary || {}) },
      formGroup: { ...base.formGroup, ...(styles.formGroup || {}) },
      label: { ...base.label, ...(styles.label || {}) },
      input: { ...base.input, ...(styles.input || {}) },
      select: { ...base.select, ...(styles.select || {}) },
      hint: base.hint,
      success: base.success
    };
  }, [styles]);

  const isPdfProof = useMemo(() => /\.pdf($|\?)/i.test(String(formData.proofUrl || '')), [formData.proofUrl]);

  if (!isOpen) return null;

  return (
    <div style={modalStyles.modal} onClick={handleClose}>
      <div style={modalStyles.modalContent} onClick={e => e.stopPropagation()}>
        <div style={modalStyles.modalHeader}>
          <h3 style={{ margin: 0, fontSize: 16, fontWeight: 600, color: colors.stone800 }}>
            {title}
          </h3>
          <button style={modalStyles.btnIcon} onClick={handleClose}>
            <FiX size={18} />
          </button>
        </div>

        <div style={modalStyles.modalBody}>
          <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 12 }}>
            <div style={modalStyles.formGroup}>
              <label style={modalStyles.label}>Payment Method</label>
              <select
                style={modalStyles.select}
                value={formData.paymentMethod}
                onChange={(e) => handleInputChange('paymentMethod', e.target.value)}
              >
                <option value="">Select</option>
                <option value="CASH">Cash</option>
                <option value="UPI">UPI</option>
                <option value="BANK_TRANSFER">Bank Transfer</option>
                <option value="CARD">Card</option>
                <option value="CHEQUE">Cheque</option>
                <option value="OTHER">Other</option>
              </select>
            </div>
            <div style={modalStyles.formGroup}>
              <label style={modalStyles.label}>Payment Date</label>
              <input
                type="date"
                style={modalStyles.input}
                value={formData.paymentDate}
                onChange={(e) => handleInputChange('paymentDate', e.target.value)}
              />
            </div>
          </div>

          <div style={modalStyles.formGroup}>
            <label style={modalStyles.label}>Reference</label>
            <input
              style={modalStyles.input}
              value={formData.paymentReference}
              onChange={(e) => handleInputChange('paymentReference', e.target.value)}
              placeholder="Txn ID / Cheque No / Notes"
            />
          </div>

          <div style={modalStyles.formGroup}>
            <label style={modalStyles.label}>Payment Proof (optional)</label>
            <input
              type="file"
              accept={acceptPdfProof ? "image/*,application/pdf" : "image/*"}
              style={modalStyles.input}
              onChange={handleFileUpload}
              disabled={formData.uploading}
            />
            {formData.uploading && (
              <div style={modalStyles.hint}>Uploading…</div>
            )}
            {!!formData.proofUrl && (
              <div style={modalStyles.success}>
                Uploaded:{' '}
                <a href={formData.proofUrl} target="_blank" rel="noreferrer" style={{ color: colors.stone700, fontWeight: 600 }}>
                  View
                </a>
                {!isPdfProof && (
                  <div style={{ marginTop: 8 }}>
                    <a href={formData.proofUrl} target="_blank" rel="noreferrer">
                      <img
                        src={formData.proofUrl}
                        alt="Payment proof"
                        style={{
                          width: 72,
                          height: 72,
                          objectFit: 'cover',
                          borderRadius: 10,
                          border: `1px solid ${colors.stone200}`
                        }}
                      />
                    </a>
                  </div>
                )}
              </div>
            )}
          </div>

          {additionalFields}
        </div>

        <div style={modalStyles.modalFooter}>
          <button
            style={modalStyles.btnSecondary}
            onClick={handleClose}
            disabled={isSubmitting}
          >
            Cancel
          </button>
          <button
            style={modalStyles.btnPrimary}
            onClick={handleSubmit}
            disabled={isSubmitting || formData.uploading}
          >
            {isSubmitting ? submitLoadingText : submitText}
          </button>
        </div>
      </div>
    </div>
  );
};

PaymentModal.propTypes = {
  isOpen: PropTypes.bool.isRequired,
  onClose: PropTypes.func.isRequired,
  onSubmit: PropTypes.func.isRequired,
  title: PropTypes.string,
  submitText: PropTypes.string,
  submitLoadingText: PropTypes.string,
  isSubmitting: PropTypes.bool,
  acceptPdfProof: PropTypes.bool,
  initialData: PropTypes.shape({
    paymentMethod: PropTypes.string,
    paymentReference: PropTypes.string,
    paymentDate: PropTypes.string,
    proofUrl: PropTypes.string
  }),
  additionalFields: PropTypes.node,
  styles: PropTypes.object
};

export default PaymentModal;